import React, { useState, useEffect } from "react";

import Select from "react-select";

import PropTypes from "prop-types";

import { format } from "date-fns";

import { useQuery } from "@apollo/client";

import { Title, Button, Field, Control, Label, Content, Input } from "rbx";
import { initialValue } from "rbx/components/dropdown/dropdown-context";

import { DateInput, Loader } from "../../../../components";
import { FIND_MANY_CODES } from "../../../../graphql/codes/codes.queries";
import { useAuth } from "../../../../context/AuthContext";

const LicenseForm = ({ onComplete, onCancel, initialInputs = {}, employeeData = {}, mobile }) => {
  const { state: authState } = useAuth();
  const [inputs, setInputs] = useState({});
  const [employeeLicenses, setEmployeeLicenses] = useState([]);
  const { data: { findManyCodes: options } = {}, loading } = useQuery(
    FIND_MANY_CODES,
    {
      variables: {
        where: {
          entity: {
            equals: "licenses",
          },
        },
        orderBy: [
          {
            title: "asc",
          },
        ],
      },
    }
  );

  useEffect(() => {
    if (employeeData?.licenses?.length > 0) {
      const licenseIds = employeeData?.licenses?.map((s) => ({licenseId: s?.id, codeId: s?.codes?.id}));
      setEmployeeLicenses(licenseIds);
    }
  }, [employeeData]);

  useEffect(() => {
    if (initialInputs?.id) {
      setInputs({
        expDate: initialInputs?.expDate || format(new Date(), "yyyy-MM-dd"),
        issueDate: initialInputs?.issueDate || format(new Date(), "yyyy-MM-dd"),
        licenseNumber: initialInputs?.licenseNumber || "",
        title: initialInputs?.title || "",
        expirationType: initialInputs?.expirationType || "manual",
      });
    }
  }, [initialInputs]);

  const handleChange = (e) => {
    const name = e?.target?.name || e?.name;
    const value = e?.target?.value || e?.value;
    setInputs({ ...inputs, [name]: value });
  };

  const handleLicenseChange = (e) => {
    setInputs({ ...inputs, ...e });
  };

  const handleBuildNewLicense = () => {
    const data = {
      licenses: {
        create: [
          {
            codes: {
              connect: {
                id: inputs?.value,
              },
            },
            issueDate: inputs?.issueDate || format(new Date(), "yyyy-MM-dd"),
            licenseNumber: inputs?.licenseNumber || "",
            ...(authState?.user?.id
              ? {
                  assignedBy: {
                    connect: { id: authState.user.id },
                  },
                }
              : {}),
          },
        ],
      },
    };

    if (inputs?.expirationType === "manual") {
      data.licenses.create[0].expDate =
        inputs?.expDate || format(new Date(), "yyyy-MM-dd");
    }
    return data;
  };

  const handleUpdateLicense = () => {
    const d = {
      licenses: {
        update: [
          {
            data: {
              issueDate: {
                set: inputs?.issueDate || format(new Date(), "yyyy-MM-dd"),
              },
              licenseNumber: {
                set: inputs?.licenseNumber || "",
              },
            },
            where: {
              id: initialInputs?.id,
            },
          },
        ],
      },
    };

    if (inputs?.expirationType === "manual") {
      d.licenses.update[0].data.expDate = {
        set: inputs?.expDate || format(new Date(), "yyyy-MM-dd"),
      };
    }

    return d;
  };

  const handleComplete = () => {
    if (initialInputs?.id) {
      onComplete(handleUpdateLicense());
    } else {
      onComplete(handleBuildNewLicense());
    }
  };

  // Filter out licenses that are already assigned to the employee
  const filteredOptions = options?.filter(option => {
    // If editing an existing license, don't filter it out
    if (initialInputs?.id) {
      return true;
    }
    // Filter out licenses that are already assigned (based on codeId)
    return !employeeLicenses.some(license => license.codeId === option.id);
  }) || [];

  if (loading && options?.length) return <Loader />;

  return (
    <div
      id="licenseForm"
      style={{
        minHeight: "60vh",
        overflow: "auto",
      }}
    >
      <header className="modal-head">
        <div className="modal-head-start">
          <Title size={5}>
            {initialInputs?.id ? "Update" : "Create"} License
          </Title>
        </div>
         {mobile && (
          <br />
        )}
        <div className="modal-head-end">
          <Button.Group hasAddons>
            <Button
              disabled={loading}
              size="small"
              type="button"
              onClick={() => onCancel()}
            >
              <span>Cancel</span>
            </Button>
            <Button
              color="primary"
              disabled={!inputs?.value && !initialInputs?.id}
              form="company-form"
              size="small"
              onClick={handleComplete}
            >
              <span>Save</span>
            </Button>
          </Button.Group>
        </div>
      </header>
      <hr />
      <Field
        style={{
          display: "flex",
          flexDirection: "column",
          justifContent: "stretch",
          alignItems: "stretch",
        }}
      >
        <Control>
          <Label textSize={5}>Title:</Label>
          {initialInputs?.id ? (
            <Content size="medium">{`${inputs?.title}`}</Content>
          ) : (
            <div style={{ width: "100%" }}>
              <Select
                options={filteredOptions?.map((o) => ({
                  value: o?.id,
                  label: `${o?.title} ${o?.state} ${o?.level}`,
                  ...o,
                }))}
                value={inputs || initialValue}
                onChange={(e) => {
                  handleLicenseChange(e);
                }}
              />
            </div>
          )}
        </Control>
        <Field
          style={{
            display: "flex",
            flexDirection: mobile ? "column": "row",
            justifyContent: "flex-start",
            gap: "1rem",
          }}
        >
          <Control>
            <Label textSize={5}>License Number</Label>
            <Input
              autoComplete="off"
              maxLength={100}
              name="licenseNumber"
              placeholder="ID"
              type="text"
              value={inputs?.licenseNumber || ""}
              onChange={(e) => handleChange(e)}
            />
          </Control>

          <Control>
            <Label textSize={5}>Issue Date:</Label>
            <DateInput
              name="issueDate"
              type="date"
              value={inputs?.issueDate || format(new Date(), "yyyy-MM-dd")}
              onChange={(e) => handleChange(e)}
            />
          </Control>

          {(inputs?.expirationType === "manual" ||
            inputs?.expirationType === "manual") && (
            <Control>
              <Label textSize={5}>Expiration Date:</Label>
              <DateInput
                name="expDate"
                type="date"
                value={inputs.expDate || format(new Date(), "yyyy-MM-dd")}
                onChange={(e) => handleChange(e)}
              />
            </Control>
          )}
        </Field>
      </Field>
    </div>
  );
};

LicenseForm.propTypes = {
  onComplete: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  initialInputs: PropTypes.object,
  employeeData: PropTypes.object,
  mobile: PropTypes.bool,
};

LicenseForm.defaultProps = {
  initialInputs: {},
  employeeData: {},
  mobile: false,
};

export default LicenseForm;
